import type { Epic, Issue, Task } from '@/features/project-board/types';
import { ThroughputSeries } from '@/features/project-board/analytics/throughput';

/** 주 단위 추세 계산 — 누적 완료(번업)와 이슈 유입/처리. 둘 다 문서 날짜 기반 근사다. */

const DAY = 86_400_000;

export interface CumulativePoint {
  start: string;
  label: string;
  /** 그 주까지의 누적 완료. */
  cumulative: number;
  /** 그 주에 완료된 수. */
  done: number;
}

export interface Burnup {
  points: CumulativePoint[];
  /** 제외를 뺀 전체 task — 100% 기준선. */
  scope: number;
  doneTotal: number;
}

export interface IssueFlowPoint {
  start: string;
  label: string;
  opened: number;
  closed: number;
}

/** 한 그래프에 같이 그리기 위한 통합 시계열 — 같은 주 구간을 공유한다. */
export interface ActivityPoint {
  start: string;
  label: string;
  /** 그 주까지의 누적 완료 task(왼쪽 축). */
  cumulative: number;
  /** 그 주에 완료된 task. */
  done: number;
  /** 그 주에 갱신된 에픽 문서 수. */
  epicUpdates: number;
  /** 그 주에 접수된 이슈(오른쪽 축). */
  opened: number;
  /** 그 주에 처리된 이슈(오른쪽 축). */
  closed: number;
}

export interface Activity {
  points: ActivityPoint[];
  /** 제외를 뺀 전체 task — 누적선의 기준선. */
  scope: number;
  doneTotal: number;
}

const label = (start: string) => `${start.slice(5, 7)}/${start.slice(8, 10)}`;

export class WeeklyTrend {
  static parseDate(value: string): Date | null {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  static weekStarts(now: Date, weeks: number): string[] {
    const current = ThroughputSeries.bucketStart(now, 'week');
    const base = new Date(`${current}T12:00:00`).getTime();
    return Array.from({ length: weeks }, (_, index) =>
      ThroughputSeries.bucketStart(new Date(base - (weeks - 1 - index) * 7 * DAY), 'week'),
    );
  }

  /**
   * 누적 완료(번업) — 기준선은 "제외를 뺀 전체 task"다.
   * 번다운(남은 일)이 아니라 번업으로 그리는 이유: 스코프가 계속 늘어나는 프로젝트라서, 남은 양만 보면
   * 일을 해도 줄지 않는 것처럼 보인다. 누적선과 기준선이 벌어진 폭이 남은 일이다.
   */
  static burnupOf(tasks: Task[], now: Date, weeks = 16): Burnup {
    const starts = WeeklyTrend.weekStarts(now, weeks);
    const dated = tasks
      .filter((task) => task.status === 'done')
      .map((task) => WeeklyTrend.parseDate(task.updatedAt))
      .filter((date): date is Date => date !== null)
      .map((date) => ThroughputSeries.bucketStart(date, 'week'));

    // 표시 구간보다 앞서 끝난 것도 누적에는 들어가야 한다.
    let running = dated.filter((value) => value < starts[0]).length;
    const points = starts.map((start) => {
      const done = dated.filter((value) => value === start).length;
      running += done;
      return { start, label: label(start), cumulative: running, done };
    });

    return {
      points,
      scope: tasks.filter((task) => task.status !== 'dropped').length,
      doneTotal: dated.length,
    };
  }

  /** 이슈 유입(접수) vs 처리(완료) — 두 선이 벌어지면 밀리고 있다는 뜻이다. */
  static issueFlowOf(issues: Issue[], now: Date, weeks = 12): IssueFlowPoint[] {
    const starts = WeeklyTrend.weekStarts(now, weeks);
    const opened = issues
      .map((issue) => WeeklyTrend.parseDate(issue.reportedAt))
      .filter((date): date is Date => date !== null)
      .map((date) => ThroughputSeries.bucketStart(date, 'week'));
    const closed = issues
      .filter((issue) => issue.status === 'done' && issue.handledAt)
      .map((issue) => WeeklyTrend.parseDate((issue.handledAt as string).slice(0, 10)))
      .filter((date): date is Date => date !== null)
      .map((date) => ThroughputSeries.bucketStart(date, 'week'));

    return starts.map((start) => ({
      start,
      label: label(start),
      opened: opened.filter((value) => value === start).length,
      closed: closed.filter((value) => value === start).length,
    }));
  }

  /**
   * task 누적 완료와 이슈 유입·처리를 **한 시간축**에 얹는다.
   *
   * 단위가 다르므로(누적 task 수 vs 주간 이슈 건수) 화면에서 축을 둘로 나눠 그린다. 굳이 합치는 이유는
   * "일을 얼마나 밀어냈는가(task)"와 "밖에서 얼마나 들어왔는가(이슈)"를 같은 주에 놓고 봐야 원인이 읽히기
   * 때문이다 — 이슈가 몰린 주에 task 완료가 꺾이는 식의 관계가 그렇게 드러난다.
   */
  static activityOf(tasks: Task[], issues: Issue[], now: Date, weeks = 16, epics: Epic[] = []): Activity {
    const burnup = WeeklyTrend.burnupOf(tasks, now, weeks);
    const flow = WeeklyTrend.issueFlowOf(issues, now, weeks);
    const epicUpdates = epics
      .map((epic) => WeeklyTrend.parseDate(epic.updatedAt ?? ''))
      .filter((date): date is Date => date !== null)
      .map((date) => ThroughputSeries.bucketStart(date, 'week'));

    return {
      scope: burnup.scope,
      doneTotal: burnup.doneTotal,
      points: burnup.points.map((point) => {
        const matched = flow.find((candidate) => candidate.start === point.start);
        return {
          start: point.start,
          label: point.label,
          cumulative: point.cumulative,
          done: point.done,
          epicUpdates: epicUpdates.filter((value) => value === point.start).length,
          opened: matched?.opened ?? 0,
          closed: matched?.closed ?? 0,
        };
      }),
    };
  }
}
